import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HydraCollectionResponse, SearchHit, SearchHitRoute, SearchHitType, SearchHitTypeConfigs, SupplierArticle, UserSettings } from 'shared';
import { environment } from '@environments/environment';

const page =
  (route: string) =>
  (hit: SearchHit): SearchHitRoute => [route, hit.entityId];

/** "/clients/56" => 56 */
const idOf = (iri: string | null | undefined) => iri?.split('/').pop() ?? null;

/**
 * The search hit types crownbase-nxt shows, in the order of the search type settings. Visit reports and agreements are
 * opened under their client, articles on their supplier article: a hit only has its own id, so those are looked up.
 */
export function crownbaseSearchHitTypes(): SearchHitTypeConfigs {
  const http = inject(HttpClient);
  const get = <T>(path: string): Observable<T> => http.get<T>(`${environment.apiUrl}${path}`);
  const underClient =
    (resource: string, tab: string) =>
    (hit: SearchHit): Observable<SearchHitRoute> =>
      get<{ client?: string | null }>(`/${resource}/${hit.entityId}`).pipe(
        map((entity) => {
          const clientId = idOf(entity.client);
          return clientId ? ['/clients', clientId, tab, hit.entityId] : null;
        }),
      );

  return {
    client_order: { label: 'CLIENT_ORDER', icon: 'rc-icon-shopping-cart', route: page('/sales/orders/client_orders'), showsDate: true },
    client_quote: { label: 'CLIENT_QUOTE', icon: 'rc-icon-quote', route: page('/sales/client_quotes'), showsDate: true },
    client_invoice: { label: 'CLIENT_INVOICE', icon: 'rc-icon-sales-invoice', route: page('/sales/client_invoices'), showsDate: true },
    client_credit_note: { label: 'CLIENT_CREDIT_NOTE', icon: 'rc-icon-credit-note-out', route: page('/sales/client_credit_notes'), showsDate: true },
    client_return: { label: 'CLIENT_RETURN', icon: 'rc-icon-return', route: page('/sales/client_returns'), showsDate: true },
    complaint: { label: 'COMPLAINT', icon: 'rc-icon-message-exclamation', route: page('/complaints'), showsDate: true },
    delivery: { label: 'DELIVERY', icon: 'rc-icon-truck-delivery', route: page('/warehouse/deliveries'), showsDate: true },
    visit_report: { label: 'VISIT_REPORT', icon: 'rc-icon-file-text', route: underClient('visit_reports', 'visit-reports'), showsDate: true },
    client_agreement: { label: 'CLIENT_AGREEMENT', icon: 'rc-icon-file-check', route: underClient('client_agreements', 'agreements'), showsDate: true },
    supplier_order: { label: 'SUPPLIER_ORDER', icon: 'rc-icon-clipboard-package', route: page('/purchase/supplier_orders'), showsDate: true },
    supplier_invoice: { label: 'SUPPLIER_INVOICE', icon: 'rc-icon-purchase-invoice', route: page('/purchase/supplier_invoices'), showsDate: true },
    supplier_credit_note: { label: 'SUPPLIER_CREDIT_NOTE', icon: 'rc-icon-credit-note-in', route: page('/purchase/supplier_credit_notes'), showsDate: true },
    client: { label: 'CLIENT', icon: 'rc-icon-id', route: page('/clients') },
    supplier: { label: 'SUPPLIER', icon: 'rc-icon-store', route: page('/suppliers') },
    // An article sold by several suppliers opens on the first of its supplier articles
    article: {
      label: 'ARTICLE',
      icon: 'rc-icon-package',
      route: (hit) =>
        get<HydraCollectionResponse<SupplierArticle>>(`/articles/${hit.entityId}/supplier_articles?itemsPerPage=1`).pipe(
          map((response) => {
            const supplierArticleId = response['hydra:member'][0]?.id;
            return supplierArticleId ? ['/supplier_articles', supplierArticleId] : null;
          }),
        ),
    },
  };
}

/**
 * The types to search by default: the user's search types (the ones crownbase-nxt can show), or all of them when they
 * have none. "Search everywhere" searches `allTypes`.
 */
export function defaultSearchTypes(settings: UserSettings | null | undefined, allTypes: SearchHitType[]): SearchHitType[] {
  const preferred = settings?.defaultSearchTypes ?? [];
  const chosen = allTypes.filter((type) => preferred.includes(type));
  return chosen.length ? chosen : allTypes;
}
